import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';

const CampaignContext = createContext(null);

const API_URL = '/api';

const getApiError = (err, fallbackMessage) => err.response?.data?.detail || fallbackMessage;

export const CampaignProvider = ({ children }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [currentCampaign, setCurrentCampaign] = useState(null);
  const [pendingRequests, setPendingRequests] = useState(0);
  const [error, setError] = useState(null);
  const loading = pendingRequests > 0;

  const withLoading = useCallback(async (action, fallbackMessage) => {
    setPendingRequests((count) => count + 1);
    setError(null);
    try {
      return await action();
    } catch (err) {
      setError(getApiError(err, fallbackMessage));
      throw err;
    } finally {
      setPendingRequests((count) => Math.max(0, count - 1));
    }
  }, []);

  // Fetch all campaigns
  const fetchCampaigns = useCallback(async () => {
    return withLoading(async () => {
      const response = await axios.get(`${API_URL}/campaigns`);
      setCampaigns(response.data);
      return response.data;
    }, 'Failed to fetch campaigns');
  }, [withLoading]);

  // Fetch single campaign
  const fetchCampaign = useCallback(async (campaignId) => {
    return withLoading(async () => {
      const response = await axios.get(`${API_URL}/campaigns/${campaignId}`);
      setCurrentCampaign(response.data);
      return response.data;
    }, 'Failed to fetch campaign');
  }, [withLoading]);

  // Create campaign
  const createCampaign = useCallback(async (data) => {
    return withLoading(async () => {
      const response = await axios.post(`${API_URL}/campaigns`, data);
      setCampaigns(prev => [...prev, response.data]);
      return response.data;
    }, 'Failed to create campaign');
  }, [withLoading]);

  // Update campaign
  const updateCampaign = useCallback(async (campaignId, data) => {
    return withLoading(async () => {
      const response = await axios.patch(`${API_URL}/campaigns/${campaignId}`, data);
      setCampaigns(prev => prev.map(c => c.id === campaignId ? response.data : c));
      setCurrentCampaign(prev => prev?.id === campaignId ? response.data : prev);
      return response.data;
    }, 'Failed to update campaign');
  }, [withLoading]);

  // Create invite
  const createInvite = useCallback(async (campaignId) => {
    return withLoading(async () => {
      const response = await axios.post(`${API_URL}/campaigns/${campaignId}/invite`);
      return response.data;
    }, 'Failed to create invite');
  }, [withLoading]);

  // Join campaign
  const joinCampaign = useCallback(async (code, characterName = '') => {
    return withLoading(async () => {
      const response = await axios.post(
        `${API_URL}/campaigns/join/${encodeURIComponent(code)}`,
        null,
        { params: { character_name: characterName } }
      );
      await fetchCampaigns();
      return response.data;
    }, 'Failed to join campaign');
  }, [fetchCampaigns, withLoading]);

  // Get user's role in current campaign
  const getUserRole = useCallback((userId) => {
    if (!currentCampaign) return null;
    const member = currentCampaign.members.find(m => m.user_id === userId);
    return member?.role || null;
  }, [currentCampaign]);

  // Check if user is GM
  const isGameMaster = useCallback((userId) => {
    const role = getUserRole(userId);
    return role === 'owner' || role === 'editor';
  }, [getUserRole]);

  const value = {
    campaigns,
    currentCampaign,
    loading,
    error,
    fetchCampaigns,
    fetchCampaign,
    createCampaign,
    updateCampaign,
    createInvite,
    joinCampaign,
    getUserRole,
    isGameMaster,
    setCurrentCampaign
  };

  return (
    <CampaignContext.Provider value={value}>
      {children}
    </CampaignContext.Provider>
  );
};

export const useCampaign = () => {
  const context = useContext(CampaignContext);
  if (!context) {
    throw new Error('useCampaign must be used within a CampaignProvider');
  }
  return context;
};

export default CampaignContext;

